import { useMemo } from 'react';

import { Card, Form } from '@arco-design/web-react';

import { ContainerStoragePreviewSection } from '../../../capabilities/container-storage/ContainerStoragePreviewSection';
import { ServicePreviewSection } from '../../../capabilities/service/ServicePreviewSection';
import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { buildContainerDisplayDescriptors } from '../headless/projections';
import type { CiSpec } from '../headless/schema';
import { CI_MODULES } from '../modules';

const PREVIEW_WATCH_FIELDS = [
  'name',
  'description',
  'namespace',
  'workspace',
  'datacenter',
  'runStrategy',
  'basic.image',
  'systemDiskSize',
  'resource',
  'volumes',
  'system',
  'network',
  '_containerDeploySharedMemory',
  '_sharedMemoryEnabled',
  'network.nics',
  '_nicEnabled',
  'optionMeta',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '未配置';
const ALL_MODULE_KEYS = CI_MODULES.map((mod) => mod.key);

function groupDescriptorsByModule(items: DisplayDescriptor[]) {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

function renderModuleContent(
  modKey: string,
  items: DisplayDescriptor[],
  services: CiSpec['resource']['network']['services'],
  volumes: CiSpec['volumes'],
) {
  const column = modKey === 'baseInfo' ? 2 : 1;

  if (modKey === 'service') {
    return <ServicePreviewSection services={services} />;
  }

  if (modKey === 'storage') {
    return <ContainerStoragePreviewSection volumes={volumes} />;
  }

  const displayItems = items.length ? items : [{ id: `${modKey}-empty`, group: modKey, label: '-', value: PREVIEW_EMPTY }];
  return <DescriptorDescriptions items={displayItems} column={column} />;
}

/** 容器实例 ResourcePreview：确认步只读摘要 */
export function CiResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: DeployResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('ContainerInstance');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const formValues = form?.getFieldsValue() ?? {};
  const skuResourceModules = formValues.skuResourceModules as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'ContainerInstance', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    namespace: ctx?.namespace ?? (formValues.namespace as string | undefined),
    cluster: ctx?.cluster ?? (formValues.datacenter as string | undefined),
    sku: { enabledModules: previewEnabledModules },
  };

  const resolvedSpec = (spec ?? plugin.pipeline.schema.parse(plugin.pipeline.fromForm(formValues, previewCtx))) as CiSpec;
  const flatItems = descriptors ?? buildContainerDisplayDescriptors(resolvedSpec, previewCtx);
  const grouped = groupDescriptorsByModule(flatItems);
  const services = resolvedSpec.resource?.network?.services;
  const visibleModules = CI_MODULES.filter((mod) => enabledModuleKeys.includes(mod.key));

  if (!visibleModules.length) {
    return null;
  }

  return (
    <>
      {visibleModules.map((mod) => (
        <Card key={mod.key} bordered={false} title={mod.title} style={{ marginBottom: 12 }}>
          {renderModuleContent(mod.key, grouped.get(mod.key) ?? [], services, resolvedSpec.volumes)}
        </Card>
      ))}
    </>
  );
}
